import { Component, ErrorInfo, ReactNode } from 'react';
import { captureFrontendError } from '../../observability/capture';

interface NotificationErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  resetKey?: string | number;
}

interface NotificationErrorBoundaryState {
  hasError: boolean;
}

/**
 * Notifications are a side channel fed by Novu, so a malformed payload or a downed service must
 * cost the user their notifications and nothing else. Without this the failure reaches the
 * app-wide ErrorBoundary, which replaces the entire application with a full-screen error page.
 */
export class NotificationErrorBoundary extends Component<
  NotificationErrorBoundaryProps,
  NotificationErrorBoundaryState
> {
  constructor(props: NotificationErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): NotificationErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(previousProps: NotificationErrorBoundaryProps): void {
    const { resetKey } = this.props;
    const { hasError } = this.state;

    if (hasError && resetKey !== previousProps.resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureFrontendError(error, {
      componentStack: errorInfo.componentStack ?? undefined,
      route: window.location.pathname,
    });

    console.error('Notification rendering failed:', error);
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { children, fallback = null } = this.props;

    return hasError ? fallback : children;
  }
}
